import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { Button } from "../../../components/button";
import { DialogV2 } from "../../../components/dialog-v2";
import { useToast } from "../../../components/toast-container";

interface EventQRCodeModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  fetchQrImage: (id: string, signal?: AbortSignal) => Promise<Blob>;
}

export const EventQRCodeModal: React.FC<EventQRCodeModalProps> = ({
  open,
  onClose,
  eventId,
  eventName,
  fetchQrImage,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [isLoadingQr, setIsLoadingQr] = useState(false);

  useEffect(() => {
    if (!open || !eventId) {
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    setIsLoadingQr(true);
    setQrImageUrl(null);

    fetchQrImage(eventId, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = window.URL.createObjectURL(blob);
        setQrImageUrl(objectUrl);
      })
      .catch((fetchError: unknown) => {
        if (controller.signal.aborted) return;
        console.error("Failed to load QR code image:", fetchError);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingQr(false);
        }
      });

    return () => {
      controller.abort();
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, eventId, fetchQrImage]);

  useEffect(() => {
    if (!open) {
      setQrImageUrl(null);
    }
  }, [open]);

  const handleSaveQRCode = useCallback(async () => {
    if (!qrImageUrl) return;
    let sourceUrl: string | null = null;
    let downloadUrl: string | null = null;
    try {
      const response = await fetch(qrImageUrl, {
        headers: {
          Accept: "image/svg+xml,image/png,image/*;q=0.9,*/*;q=0.8",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch QR code image (${response.status})`);
      }

      const blob = await response.blob();
      if (!blob.size) {
        throw new Error("QR code image is empty");
      }

      sourceUrl = window.URL.createObjectURL(blob);
      const image = new Image();
      image.decoding = "async";
      image.src = sourceUrl;
      await image.decode().catch(() => {
        return new Promise<void>((resolve, reject) => {
          image.onload = () => resolve();
          image.onerror = () => reject(new Error("Failed to decode QR code image"));
        });
      });

      const width = image.naturalWidth || 1024;
      const height = image.naturalHeight || 1024;
      if (!width || !height) {
        throw new Error("QR code image dimensions are invalid");
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Failed to create canvas context");
      }

      context.drawImage(image, 0, 0, width, height);

      const pngBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((result) => {
          if (!result) {
            reject(new Error("Failed to export PNG"));
            return;
          }
          resolve(result);
        }, "image/png");
      });

      downloadUrl = window.URL.createObjectURL(pngBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${eventName.replace(/[^a-zA-Z0-9]/g, "_")}_qr_code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addToast({
        title: t("event.qr_modal.save_success"),
        type: "success",
        message: "",
      });
    } catch (error) {
      console.error("Failed to download QR code:", error);
      addToast({
        title: t("event.qr_modal.save_error"),
        type: "error",
        message: "",
      });
    } finally {
      if (downloadUrl) {
        window.URL.revokeObjectURL(downloadUrl);
      }
      if (sourceUrl) {
        window.URL.revokeObjectURL(sourceUrl);
      }
    }
  }, [qrImageUrl, eventName, addToast, t]);

  return (
    <DialogV2
      open={open}
      onClose={onClose}
      title={t("event.qr_modal.title")}
      description={eventName}
      maxWidth="sm"
      backdropClassName="bg-[#00000033]"
      paperClassName="w-[min(100vw-32px,780px)] min-h-[80vh]"
      contentClassName="px-8 pb-0"
      titleClassName="pr-10"
      actions={
        <div className="flex flex-col gap-3">
          <Button
            onClick={handleSaveQRCode}
            disabled={!qrImageUrl}
            className="w-full bg-[#00BC7D] hover:bg-[#00a56e] text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Registration QR Code
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-6 pb-8 pt-4">
        <div className="w-full max-w-[560px] rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          {qrImageUrl ? (
            <img
              src={qrImageUrl}
              alt="Event Registration QR Code"
              className="mx-auto block h-auto w-full max-h-[58vh] object-contain"
            />
          ) : (
            <div className="flex min-h-[40vh] w-full items-center justify-center rounded-xl bg-gray-100">
              <span className="text-sm text-gray-400">
                {isLoadingQr ? t("ui.loading_data") : t("event.qr_modal.no_qr_code")}
              </span>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-500">{t("event.qr_modal.scan_instruction")}</p>
      </div>
    </DialogV2>
  );
};

export default EventQRCodeModal;
